import Add16 from '@carbon/icons-react/es/add/16';
import { Space, Typography } from 'antd';
import { concat, isEmpty, set, uniqBy } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { JiraConfig, JiraField, Option, SettingsMeta } from 'api/integrations';
import { Alert } from 'components/atoms/alert';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { Loading } from 'components/atoms/loading';
import { Modal } from 'components/atoms/modal';
import { Select } from 'components/atoms/select';
import { MiniTable } from 'components/molecules/table';
import { toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';
import {
  GET_JIRA_CONFIG,
  GET_JIRA_FIELDS,
  useQueryGetJiraFields,
  useQueryJiraIssueTypes,
} from 'queries/jira-integration';
import { FormItem } from '../styles';
import { SectionKeys } from '../types';
import { getDefaultTableColumns, getDynamicTableColumns } from './columns';
import { defaultJiraFieldIds, defaultRows } from './constants';
import { Heading } from './head';

interface FieldSettings {
  jiraConfig: JiraConfig;
  isFetching?: boolean;
  onChange: (updatedObject: SettingsMeta, key?: SectionKeys) => void;
  updating?: SectionKeys;
}

const TableLoader = () => {
  const loading = useTranslation('settings.integrations.jira.loading');

  return (
    <Alert
      message={
        <Space>
          <Loading />
          <Typography.Text>
            {loading}
            ...
          </Typography.Text>
        </Space>
      }
      type="info"
    />
  );
};

export const FieldSettings: FC<FieldSettings> = ({
  jiraConfig,
  isFetching,
  onChange,
  updating,
}) => {
  const {
    integrationId,
    settingsMeta: {
      fixedProjectId,
      defaultIssueTypeId = '',
      fields: savedFields = [],
    },
  } = jiraConfig;
  const fieldsRefreshSuccessTitle = useTranslation('success');
  const fieldsRefreshSuccessContent = useTranslation(
    'settings.integrations.jira.field_settings.refresh.success',
  );

  const [form] = Form.useForm<JiraField>();
  const queryClient = useQueryClient();
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [queryJirsIssueType, setQueryJiraIssueType] = useState(false);
  const [queryJiraFields, setQueryJiraFields] = useState(false);
  const [disableAddFields, setDisableAddFields] = useState(false);
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const handleChange = (data) => onChange(data, SectionKeys.FIELDS_MAPPING);

  useEffect(() => {
    setQueryJiraFields(
      Boolean(integrationId && fixedProjectId && defaultIssueTypeId),
    );
    setQueryJiraIssueType(Boolean(integrationId && fixedProjectId));
  }, [integrationId, fixedProjectId, defaultIssueTypeId]);

  useEffect(() => {
    if (updating) {
      if (updating === SectionKeys.FIELDS_MAPPING) {
        setLoading(true);
      } else {
        setDisabled(true);
      }
    } else {
      setLoading(false);
      setDisabled(false);
    }
  }, [updating, setLoading, setDisabled]);

  const { data: fieldsList, isFetching: fieldsLoading } = useQueryGetJiraFields(
    integrationId,
    fixedProjectId,
    defaultIssueTypeId,
    {
      enabled: queryJiraFields,
    },
  );

  const { data: issueTypes, isFetching: loadingIssueTypes } =
    useQueryJiraIssueTypes(integrationId, fixedProjectId, {
      enabled: queryJirsIssueType,
    });

  const addField = useTranslation('add_field');
  const addFieldTitle = useTranslation(
    'settings.integrations.jira.modal.title',
  );

  const onVisibilityChange = (fieldId: string, visibility: boolean) => {
    const changedField = savedFields.find(
      (field) => field.integrationFieldId === fieldId,
    );

    if (changedField) {
      changedField.isEnabled = visibility;
      handleChange({
        ...jiraConfig.settingsMeta,
        fields: uniqBy(concat(savedFields, changedField), 'integrationFieldId'),
      });
    }
  };

  const onFieldValueChange = (selectedOption: Option) => {
    if (!selectedOption) {
      return false;
    }

    const changedField = savedFields.find(
      (field) =>
        field?.integrationFieldId === selectedOption?.integrationFieldId,
    );

    if (changedField) {
      set(
        changedField,
        ['metaData', 'default'],
        selectedOption?.valueStr ?? selectedOption?.value,
      );
      onChange({
        ...jiraConfig.settingsMeta,
        fields: uniqBy(concat(savedFields, changedField), 'integrationFieldId'),
      });
    }

    return true;
  };

  const onIssueTypeChange = (selectedOption: Option) => {
    onChange({
      ...jiraConfig.settingsMeta,
      defaultIssueTypeId: selectedOption?.valueStr ?? selectedOption?.value,
    });
    queryClient.invalidateQueries(GET_JIRA_CONFIG);
    queryClient.invalidateQueries([
      GET_JIRA_FIELDS,
      integrationId,
      fixedProjectId,
      defaultIssueTypeId,
    ]);

    return true;
  };

  const onDelete = (fieldId: string) => {
    const nonDeletedFields = savedFields.filter(
      (field) => field.integrationFieldId !== fieldId,
    );

    onChange({ ...jiraConfig.settingsMeta, fields: nonDeletedFields });
  };

  const onAddingNewField = () => {
    const { fields = [] } = form.getFieldsValue(true);

    if (!isEmpty(fields)) {
      setShowAddFieldModal(false);
      onChange({
        ...jiraConfig.settingsMeta,
        fields: uniqBy(concat(savedFields, ...fields), 'integrationFieldId'),
      });
    }
  };

  const onRefresh = (_integrationId: number) => {
    queryClient
      .invalidateQueries([
        GET_JIRA_FIELDS,
        integrationId,
        fixedProjectId,
        defaultIssueTypeId,
      ])
      .then(() => {
        return toastService({
          toastContainer: {},
          title: fieldsRefreshSuccessTitle,
          message: fieldsRefreshSuccessContent,
        }).success();
      });
  };

  const dynamicTableColumns = getDynamicTableColumns({
    fieldsList: fieldsList ?? [],
    loading: loading || loadingIssueTypes,
    disabled,
    onVisibilityChange,
    onFieldValueChange,
    onDelete,
  });

  const defaultTableColumns = getDefaultTableColumns({
    issueTypes,
    loading: loading || loadingIssueTypes,
    onIssueTypeChange,
  });

  useDeepCompareEffect(() => {
    const availableFields = (fieldsList || []).filter(
      (field) =>
        !savedFields.some(
          (savedField) =>
            savedField.integrationFieldId === field.integrationFieldId,
        ),
    );

    if (!availableFields.length) {
      setDisableAddFields(true);
    } else {
      setDisableAddFields(false);
    }
  }, [savedFields, fieldsList]);

  /**
   * Settings meta gives the default values for all
   * Issu type needs an extra API call
   * based on Isseu type, i need to get fields call and re-render settings meta
   */
  return (
    <>
      <Heading
        jiraConfig={jiraConfig}
        onRefresh={onRefresh}
        loading={fieldsLoading}
      />

      {/* Default list */}
      <MiniTable
        dataSource={defaultRows.map((defaultRow) => {
          if (
            defaultRow.integrationFieldId === 'issuetype' &&
            jiraConfig.settingsMeta.defaultIssueTypeId
          ) {
            set(
              defaultRow,
              'metaData.default',
              jiraConfig.settingsMeta.defaultIssueTypeId,
            );
          }

          return defaultRow;
        })}
        columns={defaultTableColumns}
        pagination={false}
        className="default-fields-table"
      />

      {/* Dynamic fields */}
      {isFetching ? (
        <TableLoader />
      ) : (
        <MiniTable
          dataSource={
            savedFields.filter(
              (field) =>
                !defaultJiraFieldIds.includes(field.integrationFieldId),
            ) ?? []
          }
          columns={dynamicTableColumns}
          pagination={false}
          showHeader={false}
          loading={isFetching}
          className="dynamic-fields-table"
        />
      )}

      <Button
        type="link"
        icon={<Add16 />}
        iconPlacement="right"
        onClick={() => setShowAddFieldModal(true)}
        disabled={disabled || disableAddFields}
        loading={loading}
      >
        {addField}
      </Button>

      {/* Add fields Modal */}
      {/* Select value is taken from form and its value will be in SelectOption format */}
      <Modal
        title={addFieldTitle}
        visible={showAddFieldModal}
        onCancel={() => setShowAddFieldModal(false)}
        onOk={onAddingNewField}
        okText={addField}
        destroyOnClose
      >
        <Form form={form} preserve={false}>
          <FormItem name="fields">
            <Select
              isMulti
              isLoading={fieldsLoading}
              options={(fieldsList || [])
                .filter(
                  (field) =>
                    !savedFields.some(
                      (savedField) =>
                        savedField.integrationFieldId ===
                        field.integrationFieldId,
                    ),
                )
                .map((field) => ({
                  ...field,
                  value: field.integrationFieldId,
                  label: field.fieldName,
                  isEnabled: true,
                }))}
            />
          </FormItem>
        </Form>
      </Modal>
    </>
  );
};
